import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMyDashboards } from '../api/dashboards.ts';
import {
  useAsapWindow, useBillingRates, useCourtReportAccess, useRecordAsapWindow, useRecordBillingRate, useRecordVisitRate,
  useVisitRates,
} from '../api/policies.ts';
import { ApiError } from '../api/client.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { ClosingByRule } from '../components/ClosingByRule.tsx';
import { MoneyBox } from '../components/MoneyBox.tsx';
import { Staleness } from '../components/Staleness.tsx';
import { moneyText } from '../moneyInput.ts';
import { formatDate } from '../dates.ts';
import type { AsapWindowSetting, BillingRateInForce, VisitRate, VisitRatePolicies } from '../api/types.ts';
import {
  GROUP_WORDS, asapSummary, billingSummary, courtReportFaultLine, groupOf, hourlyWords, policyTab, rateCandidates,
  rateConfirmation, ratesSummary, validHourly, type PolicyTab,
} from '../policies.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';

function describe(rate: VisitRate): string {
  return `${rate.visits_per_year} a year · every ${rate.interval_days} days · ${rate.grace_days} days’ grace`;
}

/**
 * Company policies, tier 1 only: visits per year by residence type, and the ASAP window (155). P-22 (A-#248, A-#250,
 * A-#251): in tabs, "Client visit cadence" (sub-tabs Facility clients and Community clients, by Arthur's split of the
 * residence types, web/src/policies.ts) and "Tasks & documents", the tab kept in the address (?tab=facility|community|
 * tasks); only the selected tab's panel is on the page.
 *
 * **A rate on record is never changed.** Recording a new rate inserts it beside
 * the old ones with its own effective date, so every figure for a past period
 * keeps the standard that applied in it. There is no edit or delete control on
 * an existing rate, and the server has no route for either.
 *
 * The entry is not in anyone else's menu, and the page says so plainly if
 * reached by address; the server refuses regardless.
 */
export function CompanyPolicies() {
  const mine = useMyDashboards();
  const tierOne = mine.data?.tier === 1;

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Company policies</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Agency-wide standards. Each change is recorded from its effective date; standards already on
            record stay as they were, so figures for past periods keep the standard that applied then.
          </p>
        </header>
        <div className="page-body">
          {mine.error && !mine.data ? (
            <LoadFailure what="Company policies" error={mine.error} onRetry={() => void mine.refetch()} retrying={mine.isFetching} />
          ) : mine.isPending ? (
            <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
          ) : !tierOne ? (
            <div role="alert" className="p-6">
              <h2 className="text-lg font-bold text-neutral-900">Not open to your roles</h2>
              <p className="mt-2 text-neutral-700">Company policies are open to tier 1 only.</p>
              <Link
                to="/clients"
                className="mt-5 inline-flex min-h-[48px] items-center rounded-lg bg-neutral-900 px-5 font-semibold text-white"
              >
                Back to clients
              </Link>
            </div>
          ) : (
            <Policies />
          )}
        </div>
      </div>
    </div>
  );
}

function Policies() {
  const [params, setParams] = useSearchParams();
  const tab = policyTab(params.get('tab'));
  // the client-visit sub-tab last chosen, so "Client visit cadence" reopens it
  const [lastSub, setLastSub] = useState<'facility' | 'community'>(tab === 'community' ? 'community' : 'facility');
  const go = (t: PolicyTab) => {
    if (t === 'facility' || t === 'community') setLastSub(t);
    setParams({ tab: t }, { replace: true });
  };
  const cadence = tab === 'facility' || tab === 'community';
  const chip = (on: boolean) => `min-h-[44px] rounded-lg px-4 font-semibold ${on ? 'bg-neutral-900 text-white' : 'border border-neutral-300 text-neutral-800 hover:bg-neutral-50'}`;
  return (
    <>
      <CourtReportAccessFault />
      <div role="tablist" aria-label="Company policies" className="flex flex-wrap gap-2 px-4 pt-4 lg:px-5">
        <button type="button" role="tab" aria-selected={cadence} className={chip(cadence)} onClick={() => go(lastSub)}>
          Client visit cadence
        </button>
        <button type="button" role="tab" aria-selected={tab === 'tasks'} className={chip(tab === 'tasks')} onClick={() => go('tasks')}>
          Tasks &amp; documents
        </button>
        <button type="button" role="tab" aria-selected={tab === 'billing'} className={chip(tab === 'billing')} onClick={() => go('billing')}>
          Billing rates
        </button>
      </div>
      {cadence ? (
        <>
          <div role="tablist" aria-label="Client visit cadence" className="flex flex-wrap gap-2 px-4 pt-3 lg:px-5">
            {(['facility', 'community'] as const).map((g) => (
              <button key={g} type="button" role="tab" aria-selected={tab === g} className={chip(tab === g)} onClick={() => go(g)}>
                {GROUP_WORDS[g]}
              </button>
            ))}
          </div>
          <Cadence key={tab} group={tab} />
          <section className="border-t border-neutral-200 px-4 py-4 lg:px-5">
            <h2 className="text-lg font-bold text-neutral-900">Hospitalisation check-ins</h2>
            <p className="mt-0.5 text-[0.9375rem] text-neutral-600">
              How often a client in hospital is checked on will be configurable here.
            </p>
          </section>
        </>
      ) : tab === 'billing' ? (
        <BillingRates />
      ) : (
        <TasksAndDocuments />
      )}
    </>
  );
}

/**
 * 164 (D-125): a drafting role that cannot read a section the court reports draw on is a configuration fault, shown
 * here above the tabs, in words, whichever tab is open. Nothing is drawn when there is none, which is the only correct
 * state; a check that could not be made says so rather than passing.
 */
function CourtReportAccessFault() {
  const q = useCourtReportAccess(true);
  if (q.error && !q.data) {
    return (
      <div className="px-4 pt-4 lg:px-5">
        <LoadFailure what="Court report access" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} />
      </div>
    );
  }
  if (!q.data || q.data.gaps.length === 0) return null;
  return (
    <section role="alert" aria-label="Court report access"
             className="mx-4 mt-4 rounded-lg border-l-8 border-warning-edge bg-warning-bg px-4 py-3 text-warning-fg lg:mx-5">
      <h2 className="font-bold">Court report access: a configuration fault</h2>
      <ul className="mt-1 list-disc pl-5">
        {q.data.gaps.map((g) => <li key={`${g.role_name}|${g.section}`}>{courtReportFaultLine(g)}</li>)}
      </ul>
    </section>
  );
}

/** The Tasks & documents tab: what is in force, in words, then the ASAP window (155); 157's attachment rule joins it;
 *  and Section R's "Closing by rule" (176; the advisor's ruling 608, item 5) beside the ASAP window, as the cleared words place it. */
function TasksAndDocuments() {
  const q = useAsapWindow(true);
  return (
    <>
      {q.data && <p className="px-4 pt-4 text-[0.9375rem] font-semibold text-neutral-900 lg:px-5">{asapSummary(q.data, formatDate)}</p>}
      <AsapWindow />
      <ClosingByRule />
    </>
  );
}

/** One client-visit sub-tab: its residence types' rates, then the types neither tab holds, then their history. */
function Cadence({ group }: { group: 'facility' | 'community' }) {
  const rates = useVisitRates(true);
  const [opened, setOpened] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<string | null>(null);

  if (rates.error && !rates.data) {
    return (
      <LoadFailure
        what="Visit rates"
        error={rates.error}
        onRetry={() => void rates.refetch()}
        retrying={rates.isFetching}
        forbiddenIsSession={false}
      />
    );
  }
  if (!rates.data) return <p className="px-4 py-10 text-center text-neutral-600">Loading visit rates…</p>;

  const data = rates.data;
  const shown = data.types.filter((t) => groupOf(t.code) === group);
  const other = data.types.filter((t) => groupOf(t.code) === 'other');
  const history = [...shown, ...other]
    .flatMap((t) => [...t.scheduled, ...(t.current ? [t.current] : []), ...t.earlier].map((r) => ({ type: t, rate: r })))
    .sort((a, b) =>
      b.rate.effective_from.localeCompare(a.rate.effective_from) ||
      a.type.label.localeCompare(b.type.label));

  const row = (type: VisitRatePolicies['types'][number]) => (
    <li key={type.code} className="px-3 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900">{type.label}</p>
          <p className="text-[0.9375rem] text-neutral-700">
            {type.current
              ? <>{describe(type.current)} <span className="text-neutral-500">· since {formatDate(type.current.effective_from)}</span></>
              : 'No rate in force today'}
          </p>
          {type.scheduled.map((s) => (
            <p key={s.id} className="text-sm text-neutral-600">
              Then {describe(s)} from {formatDate(s.effective_from)}
            </p>
          ))}
        </div>
        {opened !== type.code && (
          <button
            type="button"
            onClick={() => { setOpened(type.code); setRecorded(null); }}
            className="min-h-[44px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-800
                       hover:bg-neutral-50"
          >
            Record a new rate
          </button>
        )}
      </div>
      {opened === type.code && (
        <NewRateForm
          type={type}
          today={data.today}
          onCancel={() => setOpened(null)}
          onRecorded={(message) => { setOpened(null); setRecorded(message); }}
        />
      )}
    </li>
  );

  return (
    <>
      <Staleness
        updatedAt={rates.dataUpdatedAt}
        isFetching={rates.isFetching}
        error={rates.error}
        onRefresh={() => void rates.refetch()}
      />

      <section className="px-4 py-4 lg:px-5">
        <h2 className="text-lg font-bold text-neutral-900">{GROUP_WORDS[group]}: visits per year, by residence type</h2>
        <p className="mt-1 text-[0.9375rem] font-semibold text-neutral-900">{ratesSummary(shown)}</p>
        <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-600">
          How often a client must be seen, by the kind of place they live in. A court order can raise it
          for one client; nothing here lowers what a court ordered.
        </p>
        {recorded && (
          <p role="status" className="mt-3 rounded-lg bg-note-bg px-3 py-2 text-[0.9375rem] text-note-fg ring-1 ring-note-edge">
            {recorded}
          </p>
        )}

        <ul className="mt-3 divide-y divide-neutral-200 rounded-lg border border-neutral-200">
          {shown.map(row)}
        </ul>
        {other.length > 0 && (
          <>
            <h3 className="mt-4 text-sm font-bold tracking-[0.12em] text-neutral-500 uppercase">{GROUP_WORDS.other}</h3>
            <ul className="mt-2 divide-y divide-neutral-200 rounded-lg border border-neutral-200">
              {other.map(row)}
            </ul>
          </>
        )}
      </section>

      <section className="border-t border-neutral-200 px-4 py-4 lg:px-5">
        <h2 className="text-lg font-bold text-neutral-900">History</h2>
        <p className="mt-0.5 text-sm text-neutral-600">Every visit rate on record, newest effective date first.</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[40rem] text-left text-[0.9375rem]">
            <thead className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">
              <tr>
                <th className="py-2 pr-4">Residence type</th>
                <th className="py-2 pr-4">Rate</th>
                <th className="py-2 pr-4">Effective from</th>
                <th className="py-2 pr-4">Policy</th>
                <th className="py-2">Recorded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {history.map(({ type, rate }) => (
                <tr key={rate.id} className="align-top">
                  <td className="py-2 pr-4 text-neutral-900">{type.label}</td>
                  <td className="py-2 pr-4 text-neutral-800">{describe(rate)}</td>
                  <td className="py-2 pr-4 tabular-nums text-neutral-800">
                    {formatDate(rate.effective_from)}
                    {rate.effective_from > data.today && <span className="block text-xs text-neutral-500">scheduled</span>}
                  </td>
                  <td className="py-2 pr-4 text-neutral-700">
                    {rate.policy_name ?? '—'}
                    {rate.adopted_reason && <span className="block max-w-[32rem] text-sm text-neutral-500">{rate.adopted_reason}</span>}
                  </td>
                  {/* created_at is an instant, so converting it to the viewer's zone is correct. */}
                  <td className="py-2 tabular-nums text-neutral-600">{new Date(rate.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </>
  );
}

function NewRateForm({
  type,
  today,
  onCancel,
  onRecorded,
}: {
  type: VisitRatePolicies['types'][number];
  today: string;
  onCancel: () => void;
  onRecorded: (message: string) => void;
}) {
  const record = useRecordVisitRate();
  const base = type.current;
  // Empty, not 4 / 91 / 14.
  //
  // This was the one place in the application that INVENTED a cadence. A type
  // with a rate in force is pre-filled from it, which is right — most changes
  // are an adjustment to what is there. A type with none had the current
  // agency policy typed into the form as though it had been chosen, and
  // nothing on screen said it was a guess; pressing Record would have written
  // today's cadence into `visit_target` as a decision nobody made.
  //
  // Blank fields make the person state the rate. That is the whole point of
  // this screen.
  const [visits, setVisits] = useState(base ? String(base.visits_per_year) : '');
  const [interval, setInterval] = useState(base ? String(base.interval_days) : '');
  const [grace, setGrace] = useState(base ? String(base.grace_days) : '');
  const [from, setFrom] = useState(today);
  const [policy, setPolicy] = useState(base?.policy_name ?? '');
  const [reason, setReason] = useState('');

  const perYear = Number(visits);
  // Each field must have been TYPED. Number('') is 0, which used to satisfy
  // `grace >= 0` — so a blank grace box submitted a zero nobody chose. With
  // the boxes no longer pre-filled that is the difference between "no grace"
  // and "not stated".
  const stated = (s: string) => s.trim() !== '' && Number.isInteger(Number(s));
  const valid =
    stated(visits) && perYear >= 1 && perYear <= 365 &&
    stated(interval) && Number(interval) >= 1 &&
    stated(grace) && Number(grace) >= 0 &&
    from >= today;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    record.mutate(
      {
        residence_type_code: type.code,
        visits_per_year: perYear,
        interval_days: Number(interval),
        grace_days: Number(grace),
        effective_from: from,
        policy_name: policy.trim() || null,
        adopted_reason: reason.trim() || null,
      },
      {
        onSuccess: (res) =>
          onRecorded(`${res.residence_type_label}: ${describe(res.rate)} from ${formatDate(res.rate.effective_from)}, recorded.`),
      },
    );
  };

  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-lg bg-neutral-50 p-3">
      <p className="text-sm text-neutral-700">
        A new rate for <span className="font-semibold">{type.label}</span>, recorded beside the current one.
        The current rate stays on record and stays in force until the new one takes effect.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className={label} htmlFor={`rate-visits-${type.code}`}>Visits per year</label>
          <input id={`rate-visits-${type.code}`} type="number" min={1} max={365} step={1} required
                 value={visits} onChange={(e) => setVisits(e.target.value)} className={field} />
        </div>
        <div>
          <label className={label} htmlFor={`rate-interval-${type.code}`}>Longest gap between visits (days)</label>
          <input id={`rate-interval-${type.code}`} type="number" min={1} step={1} required
                 value={interval} onChange={(e) => setInterval(e.target.value)} className={field} />
          {perYear >= 1 && (
            <p className="mt-1 text-xs text-neutral-500">
              At {perYear} a year, visits fall about {Math.round(365 / perYear)} days apart.
            </p>
          )}
        </div>
        <div>
          <label className={label} htmlFor={`rate-grace-${type.code}`}>Grace (days)</label>
          <input id={`rate-grace-${type.code}`} type="number" min={0} step={1} required
                 value={grace} onChange={(e) => setGrace(e.target.value)} className={field} />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor={`rate-from-${type.code}`}>Takes effect</label>
          <input id={`rate-from-${type.code}`} type="date" min={today} required
                 value={from} onChange={(e) => setFrom(e.target.value)} className={field} />
          <p className="mt-1 text-xs text-neutral-500">Today or later. An earlier date would restate past figures.</p>
        </div>
        <div>
          <label className={label} htmlFor={`rate-policy-${type.code}`}>Policy name</label>
          <input id={`rate-policy-${type.code}`} maxLength={48}
                 value={policy} onChange={(e) => setPolicy(e.target.value)} className={field} />
        </div>
      </div>
      <div>
        <label className={label} htmlFor={`rate-reason-${type.code}`}>Why it changed</label>
        <textarea id={`rate-reason-${type.code}`} rows={2} maxLength={2000}
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-base text-neutral-900" />
      </div>

      {record.error && (
        <div role="alert" className="rounded-lg border-l-8 border-critical-edge bg-critical-bg px-4 py-3 text-critical-fg">
          <p className="font-semibold">{record.error.message}</p>
          {record.error instanceof ApiError && record.error.hint && (
            <p className="mt-1 text-[0.9375rem] opacity-95">{record.error.hint}</p>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!valid || record.isPending}
          className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed"
        >
          {record.isPending ? 'Recording…' : 'Record rate'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[48px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

const WINDOWS = [7, 14, 30] as const;
const windowWords = (w: AsapWindowSetting) => `${w.days} days`;

/**
 * The ASAP window (155, Arthur, A-#211 d): a task the system made shows ASAP while it is due within this many days,
 * or overdue. A setting on record is never changed, not even to correct it: a new one is recorded, from today or a
 * later date, and the latest recorded for a day is the one in force. The server refuses anyone not tier 1.
 */
function AsapWindow() {
  const q = useAsapWindow(true);
  const record = useRecordAsapWindow();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState<7 | 14 | 30 | null>(null);
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const [recorded, setRecorded] = useState<string | null>(null);

  if (q.error && !q.data) {
    return (
      <section className="border-t border-neutral-200 px-4 py-4 lg:px-5">
        <LoadFailure what="The ASAP window" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching}
                     forbiddenIsSession={false} />
      </section>
    );
  }
  if (!q.data) return <p className="px-4 py-6 text-center text-neutral-600">Loading the ASAP window…</p>;
  const data = q.data;
  const start = from || data.today;
  const valid = days !== null && start >= data.today;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || days === null) return;
    record.mutate({ days, effective_from: start, reason: reason.trim() || null }, {
      onSuccess: (res) => {
        setOpen(false); setDays(null); setFrom(''); setReason('');
        setRecorded(`ASAP window: ${windowWords(res.setting)} from ${formatDate(res.setting.effective_from)}, recorded.`);
      },
    });
  };

  return (
    <section className="border-t border-neutral-200 px-4 py-4 lg:px-5">
      <h2 className="text-lg font-bold text-neutral-900">Tasks: the ASAP window</h2>
      <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-600">
        A task the system made shows ASAP while it is due within this many days, or overdue. Only a person sets
        Emergency, and a priority a person sets always stands. A setting on record is never changed: record a new one.
      </p>
      {recorded && (
        <p role="status" className="mt-3 rounded-lg bg-note-bg px-3 py-2 text-[0.9375rem] text-note-fg ring-1 ring-note-edge">
          {recorded}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-neutral-200 px-3 py-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900">
            {data.current ? windowWords(data.current) : 'No window in force today'}
            {data.current && <span className="font-normal text-neutral-500"> · since {formatDate(data.current.effective_from)}</span>}
          </p>
          {data.scheduled.map((s) => (
            <p key={s.id} className="text-sm text-neutral-600">Then {windowWords(s)} from {formatDate(s.effective_from)}</p>
          ))}
        </div>
        {!open && (
          <button type="button" onClick={() => { setOpen(true); setRecorded(null); }}
                  className="min-h-[44px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-800 hover:bg-neutral-50">
            Record a new setting
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="mt-3 space-y-3 rounded-lg bg-neutral-50 p-3">
          <div role="radiogroup" aria-label="ASAP window" className="flex flex-wrap gap-2">
            {WINDOWS.map((w) => (
              <button key={w} type="button" role="radio" aria-checked={days === w} onClick={() => setDays(w)}
                      className={`min-h-[44px] rounded-lg px-4 font-semibold ${days === w
                        ? 'bg-neutral-900 text-white' : 'border border-neutral-300 bg-white text-neutral-800'}`}>
                {w} days
              </button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={label} htmlFor="asap-from">Takes effect</label>
              <input id="asap-from" type="date" min={data.today} required value={start}
                     onChange={(e) => setFrom(e.target.value)} className={field} />
              <p className="mt-1 text-xs text-neutral-500">Today or later.</p>
            </div>
            <div>
              <label className={label} htmlFor="asap-reason">Why it changed</label>
              <input id="asap-reason" maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} className={field} />
            </div>
          </div>
          {record.error && (
            <div role="alert" className="rounded-lg border-l-8 border-critical-edge bg-critical-bg px-4 py-3 text-critical-fg">
              <p className="font-semibold">{record.error.message}</p>
              {record.error instanceof ApiError && record.error.hint && (
                <p className="mt-1 text-[0.9375rem] opacity-95">{record.error.hint}</p>
              )}
            </div>
          )}
          <div className="flex gap-3">
            <button type="submit" disabled={!valid || record.isPending}
                    className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed">
              {record.isPending ? 'Recording…' : 'Record setting'}
            </button>
            <button type="button" onClick={() => setOpen(false)}
                    className="min-h-[48px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-700">
              Cancel
            </button>
          </div>
        </form>
      )}

      {data.earlier.length > 0 && (
        <ul className="mt-3 text-sm text-neutral-600">
          {data.earlier.map((s) => (
            <li key={s.id}>{windowWords(s)} from {formatDate(s.effective_from)}{s.reason ? ` · ${s.reason}` : ''}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Who a rate is for, in words: the role, or the person and the role whose rate theirs overrides. */
const subjectWords = (r: BillingRateInForce) =>
  r.subject === 'role' ? r.role_name : `${r.staff_name ?? 'A person'} (overrides ${r.role_name}'s rate)`;
const subjectKey = (r: BillingRateInForce) => (r.subject === 'role' ? `role:${r.role_code}` : `person:${r.staff_user_id}`);

/**
 * Billing rates (165, money phase 2), tier 1 only: an hourly rate per role, and a person's own rate that overrides
 * their roles'. A rate on record is never changed: a new one is recorded from a date, and time already recorded keeps
 * the rate it had. A role's or a person's FIRST rate may start on an earlier date; every later one today or later.
 * The server refuses anyone not tier 1 and checks every rule again.
 */
function BillingRates() {
  const q = useBillingRates(true);
  const [opened, setOpened] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<string | null>(null);

  if (q.error && !q.data) {
    return (
      <section className="px-4 py-4 lg:px-5">
        <LoadFailure what="Billing rates" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching}
                     forbiddenIsSession={false} />
      </section>
    );
  }
  if (!q.data) return <p className="px-4 py-10 text-center text-neutral-600">Loading billing rates…</p>;
  const data = q.data;
  const roles = data.rates.filter((r) => r.subject === 'role');
  const people = data.rates.filter((r) => r.subject === 'person');
  const hasHistory = (r: BillingRateInForce) => data.history.some((h) =>
    r.subject === 'role' ? h.subject === 'role' && h.role_code === r.role_code : h.subject === 'person' && h.staff_user_id === r.staff_user_id);

  const row = (r: BillingRateInForce) => {
    const key = subjectKey(r);
    return (
      <li key={key} className="px-3 py-3" data-billing={key}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-neutral-900">{subjectWords(r)}</p>
            <p className="text-[0.9375rem] text-neutral-700">
              {hourlyWords(r.hourly_usd)}
              {r.effective_from && <span className="text-neutral-600"> · since {formatDate(r.effective_from)}</span>}
            </p>
            {r.next_hourly_usd && r.next_effective_from && (
              <p className="text-sm text-neutral-600">Then {hourlyWords(r.next_hourly_usd)} from {formatDate(r.next_effective_from)}</p>
            )}
          </div>
          {opened !== key && (
            <button type="button" onClick={() => { setOpened(key); setRecorded(null); }}
                    className="min-h-[44px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-800 hover:bg-neutral-50">
              Set a new rate
            </button>
          )}
        </div>
        {opened === key && (
          <BillingRateForm subject={r} today={data.today} first={!hasHistory(r)}
                           onCancel={() => setOpened(null)}
                           onRecorded={(m) => { setOpened(null); setRecorded(m); }} />
        )}
      </li>
    );
  };

  return (
    <>
      <Staleness updatedAt={q.dataUpdatedAt} isFetching={q.isFetching} error={q.error} onRefresh={() => void q.refetch()} />
      <section className="px-4 py-4 lg:px-5">
        <h2 className="text-lg font-bold text-neutral-900">Billing rates, per hour</h2>
        <p className="mt-1 text-[0.9375rem] font-semibold text-neutral-900">{billingSummary(data.rates)}</p>
        <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-600">
          Time is priced at the rate of the highest-rated role the person held that day, unless the person has their own
          rate. A rate on record is never changed: set a new one from a date, and time already recorded keeps its rate.
          Time with no rate is shown as no rate, never as nothing owed.
        </p>
        {recorded && (
          <p role="status" className="mt-3 rounded-lg bg-note-bg px-3 py-2 text-[0.9375rem] text-note-fg ring-1 ring-note-edge">
            {recorded}
          </p>
        )}
        <ul className="mt-3 divide-y divide-neutral-200 rounded-lg border border-neutral-200">{roles.map(row)}</ul>
        {people.length > 0 && (
          <>
            <h3 className="mt-4 text-sm font-bold tracking-[0.12em] text-neutral-600 uppercase">People with their own rate</h3>
            <ul className="mt-2 divide-y divide-neutral-200 rounded-lg border border-neutral-200">{people.map(row)}</ul>
          </>
        )}
        <OwnRatePicker data={data} hasHistory={hasHistory}
                       onRecorded={(m) => { setOpened(null); setRecorded(m); }} onOpen={() => { setOpened(null); setRecorded(null); }} />
      </section>

      <section className="border-t border-neutral-200 px-4 py-4 lg:px-5">
        <h2 className="text-lg font-bold text-neutral-900">History</h2>
        <p className="mt-0.5 text-sm text-neutral-600">Every billing rate on record, newest effective date first.</p>
        {data.history.length === 0 ? (
          <p className="mt-3 text-[0.9375rem] text-neutral-700">No billing rate has been recorded yet.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[40rem] text-left text-[0.9375rem]">
              <thead className="text-xs font-bold tracking-[0.12em] text-neutral-600 uppercase">
                <tr>
                  <th className="py-2 pr-4">For</th>
                  <th className="py-2 pr-4">Rate</th>
                  <th className="py-2 pr-4">Effective from</th>
                  <th className="py-2 pr-4">Why</th>
                  <th className="py-2">Recorded by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {data.history.map((h) => (
                  <tr key={h.id} className="align-top">
                    <td className="py-2 pr-4 text-neutral-900">{h.subject === 'role' ? h.role_name : h.staff_name}</td>
                    <td className="py-2 pr-4 tabular-nums text-neutral-800">{hourlyWords(h.hourly_usd)}</td>
                    <td className="py-2 pr-4 tabular-nums text-neutral-800">
                      {formatDate(h.effective_from)}
                      {h.effective_from > data.today && <span className="block text-xs text-neutral-600">scheduled</span>}
                    </td>
                    <td className="py-2 pr-4 max-w-[32rem] text-neutral-700">{h.reason}</td>
                    <td className="py-2 text-neutral-700">{h.recorded_by ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * A-#392: give ANY active person a rate of their own, chosen by name from the staff list the server sends (never an id
 * typed in). Everyone active is offered, with or without a rate of their own already (rateCandidates()); the form is
 * the same one, a first rate allowed an earlier date.
 */
function OwnRatePicker({ data, hasHistory, onRecorded, onOpen }: {
  data: NonNullable<ReturnType<typeof useBillingRates>['data']>;
  hasHistory: (r: BillingRateInForce) => boolean;
  onRecorded: (message: string) => void; onOpen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState('');
  const candidates = rateCandidates(data.people, data.rates);
  const person = candidates.find((c) => c.id === chosen);
  const subject: BillingRateInForce | null = person ? {
    subject: 'person', role_code: '', role_name: person.role_name, staff_user_id: person.id, staff_name: person.name,
    hourly_usd: null, effective_from: null, next_hourly_usd: null, next_effective_from: null,
  } : null;
  if (!open) {
    return (
      <button type="button" onClick={() => { setOpen(true); onOpen(); }}
              className="mt-4 min-h-[44px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-800 hover:bg-neutral-50">
        Give a person their own rate
      </button>
    );
  }
  return (
    <div className="mt-4 rounded-lg border border-neutral-200 p-3">
      <label className={label} htmlFor="own-rate-person">Person</label>
      <select id="own-rate-person" value={chosen} onChange={(e) => setChosen(e.target.value)} className={field}>
        <option value="">Choose a person…</option>
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} ({c.role_name}){c.has_own_rate ? ': already has a rate of their own' : ''}
          </option>
        ))}
      </select>
      {subject ? (
        <BillingRateForm key={subject.staff_user_id} subject={subject} today={data.today} first={!hasHistory(subject)}
                         onCancel={() => { setOpen(false); setChosen(''); }}
                         onRecorded={(m) => { setOpen(false); setChosen(''); onRecorded(m); }} />
      ) : (
        <button type="button" onClick={() => { setOpen(false); setChosen(''); }}
                className="mt-3 min-h-[48px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-700">
          Cancel
        </button>
      )}
    </div>
  );
}

function BillingRateForm({ subject, today, first, onCancel, onRecorded }: {
  subject: BillingRateInForce; today: string;
  /** No rate on record yet for this role or person: it may start on an earlier date (Q1). */
  first: boolean;
  onCancel: () => void; onRecorded: (message: string) => void;
}) {
  const record = useRecordBillingRate();
  const [hourly, setHourly] = useState('');
  const [from, setFrom] = useState(today);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const who = subject.subject === 'role' ? subject.role_name : (subject.staff_name ?? 'this person');
  const id = subjectKey(subject).replace(/[^a-z0-9]/gi, '-');
  const valid = validHourly(hourly) && from !== '' && (first || from >= today) && reason.trim() !== '';
  // F-100: the rate as the API takes it, however it was typed ("$1,085" sends "1085")
  const usd = moneyText(hourly, { maxDigits: 8 }) ?? '';

  const save = () => {
    if (!valid || record.isPending) return;
    const target = subject.subject === 'role' ? { role: subject.role_code } : { staffUserId: subject.staff_user_id! };
    record.mutate({ ...target, hourlyUsd: usd, effectiveFrom: from, reason: reason.trim() }, {
      onSuccess: (res) => onRecorded(`${who}: ${hourlyWords(res.rate.hourly_usd)} from ${formatDate(res.rate.effective_from)}, recorded.`),
    });
  };

  return (
    // A div, not a form: Enter in a box must never record a rate before its confirmation.
    <div className="mt-3 space-y-3 rounded-lg bg-neutral-50 p-3">
      <p className="text-sm text-neutral-700">
        A new rate for <span className="font-semibold">{who}</span>, recorded beside the current one. The current rate stays on
        record and stays in force until the new one takes effect.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor={`bill-hourly-${id}`}>Dollars an hour</label>
          <MoneyBox id={`bill-hourly-${id}`} required maxDigits={8} className={field} value={hourly}
                    onChange={(v) => { setHourly(v); setConfirming(false); }} />
        </div>
        <div>
          <label className={label} htmlFor={`bill-from-${id}`}>Takes effect</label>
          <input id={`bill-from-${id}`} type="date" min={first ? undefined : today} required value={from}
                 onChange={(e) => { setFrom(e.target.value); setConfirming(false); }} className={field} />
          <p className="mt-1 text-xs text-neutral-600">
            {first ? 'This is the first rate here, so it may start on an earlier date.' : 'Today or later: time already recorded keeps its rate.'}
          </p>
        </div>
      </div>
      <div>
        <label className={label} htmlFor={`bill-reason-${id}`}>Why (required)</label>
        <textarea id={`bill-reason-${id}`} rows={2} maxLength={2000} value={reason}
                  onChange={(e) => { setReason(e.target.value); setConfirming(false); }}
                  className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-base text-neutral-900" />
      </div>
      {record.error && (
        <div role="alert" className="rounded-lg border-l-8 border-critical-edge bg-critical-bg px-4 py-3 text-critical-fg">
          <p className="font-semibold">{record.error.message}</p>
          {record.error instanceof ApiError && record.error.hint && <p className="mt-1 text-[0.9375rem]">{record.error.hint}</p>}
        </div>
      )}
      {confirming && valid ? (
        <div role="alert" className="rounded-lg border-2 border-neutral-300 bg-panel p-3 text-neutral-900">
          <p className="font-semibold">{rateConfirmation(usd, who, from, formatDate)}</p>
          <div className="mt-2 flex flex-wrap gap-3">
            <button type="button" disabled={record.isPending} onClick={save}
                    className="min-h-[48px] rounded-lg bg-neutral-900 px-4 font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600">
              {record.isPending ? 'Recording…' : 'Yes, record this rate'}
            </button>
            <button type="button" onClick={() => setConfirming(false)}
                    className="min-h-[48px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-700">
              Not yet
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-3">
          <button type="button" disabled={!valid} onClick={() => setConfirming(true)}
                  className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed">
            Record rate…
          </button>
          <button type="button" onClick={onCancel}
                  className="min-h-[48px] rounded-lg border border-neutral-300 px-4 font-semibold text-neutral-700">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
